import { logger } from './logger';
import type { AICXEventHandler, AICXEventMap, AICXEventType } from '../types/event';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener = (...args: any[]) => void;

/**
 * 타입 안전한 이벤트 발행/구독 관리자
 *
 * 외부 API (SDK 소비자용):
 * - on(): 이벤트 구독, 구독 해제 함수 반환
 * - off(): 이벤트 구독 해제
 *
 * 내부 API (AICX 클래스 전용):
 * - emit(): 이벤트 발행
 * - removeAllListeners(): 전체 리스너 정리 (destroy 시)
 */
class EventEmitterClass {
  private listeners = new Map<AICXEventType, Set<Listener>>();

  /** 이벤트를 구독하고 구독 해제 함수를 반환 */
  on<T extends AICXEventType>(event: T, handler: AICXEventHandler<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    logger.log('이벤트 리스너 등록', { event });

    return () => this.off(event, handler);
  }

  /** 특정 이벤트의 핸들러를 구독 해제 */
  off<T extends AICXEventType>(event: T, handler: AICXEventHandler<T>): void {
    this.listeners.get(event)?.delete(handler);
    logger.log('이벤트 리스너 해제', { event });
  }

  /** 이벤트를 발행하여 등록된 모든 핸들러를 실행 (핸들러 에러는 격리) */
  emit<T extends AICXEventType>(event: T, ...args: Parameters<AICXEventMap[T]>): void {
    const handlers = this.listeners.get(event);
    if (!handlers?.size) return;

    handlers.forEach((handler) => {
      try {
        handler(...args);
      } catch (error) {
        logger.error('이벤트 핸들러 실행 중 에러', error);
      }
    });
  }

  /** 모든 이벤트 리스너 제거 (SDK destroy 시 호출) */
  removeAllListeners(): void {
    this.listeners.clear();
  }
}

/** EventEmitter 싱글톤 인스턴스 */
export const eventEmitter = new EventEmitterClass();
